import React, { useState, useRef, useEffect } from 'react';
import { Paperclip, X, Download, FileText, Image, File, Upload, Loader2 } from 'lucide-react';
import { clientsAPI, jobsAPI, usersAPI } from '../services/api';

const ALLOWED_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'doc', 'docx', 'xls', 'xlsx', 'txt', 'csv'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const getFileIcon = (contentType) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType === 'application/pdf') return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Get the appropriate API based on entity type
const getAPI = (entityType) => {
  switch (entityType) {
    case 'client':
      return clientsAPI;
    case 'project':
      return jobsAPI;
    case 'user':
      return usersAPI;
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
};

/**
 * Reusable document attachments component for clients, projects, and users
 * @param {string} entityType - 'client', 'project', or 'user'
 * @param {string} entityId - The ID of the entity
 * @param {boolean} readOnly - If true, hide upload/delete buttons
 */
const DocumentAttachments = ({ 
  entityType, 
  entityId,
  readOnly = false
}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState('');
  const fileInputRef = useRef(null);
  
  const api = getAPI(entityType);

  // Fetch documents on mount
  useEffect(() => {
    if (entityId) {
      fetchDocuments();
    }
  }, [entityId, entityType]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.getDocuments(entityId);
      setDocuments(response.data || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    setError(null);
    
    for (const file of files) {
      // Validate file extension
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        setError(`File type .${ext} not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      
      await uploadFile(file);
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setDescription('');
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const response = await api.uploadDocument(entityId, file, description);
      setDocuments(prev => [...prev, response.data]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.deleteDocument(entityId, documentId);
      setDocuments(prev => prev.filter(d => d.id !== documentId));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleDownload = async (document) => {
    try {
      const response = await api.downloadDocument(entityId, document.id);
      const url = window.URL.createObjectURL(response.data);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = document.original_filename;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`${entityType}-documents`}>
      {/* Upload Section */}
      {!readOnly && (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              data-testid="document-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors disabled:opacity-50"
              data-testid="upload-document-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              Upload Document
            </button>
            <span className="text-sm text-slate-500">
              Max 10MB. PDF, Images, Word, Excel, Text, CSV
            </span>
          </div>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Document description (optional)"
            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="document-description-input"
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
          <button 
            onClick={() => setError(null)} 
            className="ml-2 text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4 inline" />
          </button>
        </div>
      )}

      {/* Documents List */}
      {documents.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-600 flex items-center gap-2">
            <Paperclip className="w-4 h-4" />
            Documents ({documents.length})
          </h4>
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden">
            {documents.map((doc) => {
              const FileIcon = getFileIcon(doc.content_type);
              return (
                <div 
                  key={doc.id} 
                  className="flex items-center gap-3 p-3 bg-white hover:bg-slate-50"
                  data-testid={`document-${doc.id}`}
                >
                  <FileIcon className="w-5 h-5 text-slate-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {doc.original_filename}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>{formatFileSize(doc.size)}</span>
                      {doc.uploaded_at && (
                        <>
                          <span>•</span>
                          <span>{formatDate(doc.uploaded_at)}</span>
                        </>
                      )}
                    </div>
                    {doc.description && (
                      <p className="text-xs text-slate-600 mt-1 truncate">
                        {doc.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => handleDownload(doc)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Download"
                      data-testid={`download-document-${doc.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    {!readOnly && (
                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-document-${doc.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="text-sm text-slate-500 italic py-2">
          No documents attached
        </div>
      )}
    </div>
  );
};

export default DocumentAttachments;
